import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PostAdd, Save } from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import Toast from "../../components/Layout/Toast";
import { ClientTypes } from "../auth/types/authType";
import { fetchCompanies, selectSetups } from "../setups/setupSlices";
import { selectPreference } from "../preferences/preferencesSlice";
import {
  createInvoice,
  fetchUninvoicedEmployees,
  selectInvoices,
} from "./invoiceSlices";
import { SectionCard } from "./components/SectionCard";

type LineDraft = {
  selected: boolean;
  owedAmount: string;
};

export const InvoiceEntry = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { locale } = useAppSelector(selectPreference);
  const { uninvoicedEmployees, loading, success, error } =
    useAppSelector(selectInvoices);
  const {
    companiesWithCount: { companies },
  } = useAppSelector(selectSetups);

  const [agentId, setAgentId] = useState<string>("");
  const [issueDate, setIssueDate] = useState<Date | null>(new Date());
  const [remark, setRemark] = useState("");
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [saving, setSaving] = useState(false);

  const agents = companies.filter((c) => c.type === ClientTypes.ForeignAgent);
  const selectedAgent = agents.find((a) => a._id === agentId);

  useEffect(() => {
    dispatch(fetchCompanies({ searchText: "", skip: 0, take: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    setDrafts({});
    if (agentId) {
      dispatch(fetchUninvoicedEmployees(agentId));
    }
  }, [dispatch, agentId]);

  const defaultOwed = (employeeId: string): string => {
    const employee = uninvoicedEmployees.find((e) => e._id === employeeId);
    const amount =
      employee?.agentSettlement?.owedAmount ??
      selectedAgent?.owedAmountPerEmployee;
    return amount != null ? String(amount) : "";
  };

  const draftFor = (employeeId: string): LineDraft =>
    drafts[employeeId] ?? { selected: false, owedAmount: defaultOwed(employeeId) };

  const setDraft = (employeeId: string, patch: Partial<LineDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [employeeId]: { ...draftFor(employeeId), ...patch },
    }));
  };

  const selectedIds = uninvoicedEmployees
    .map((e) => e._id)
    .filter((id) => draftFor(id).selected);

  const totalOwed = useMemo(
    () =>
      selectedIds.reduce(
        (sum, id) => sum + (Number(draftFor(id).owedAmount) || 0),
        0,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drafts, uninvoicedEmployees],
  );

  const allSelected =
    uninvoicedEmployees.length > 0 &&
    selectedIds.length === uninvoicedEmployees.length;

  const toggleAll = () => {
    const next: Record<string, LineDraft> = {};
    for (const employee of uninvoicedEmployees) {
      next[employee._id] = {
        ...draftFor(employee._id),
        selected: !allSelected,
      };
    }
    setDrafts(next);
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await dispatch(
      createInvoice({
        agentId,
        issueDate: issueDate ?? undefined,
        remark: remark || undefined,
        lineItems: selectedIds.map((employeeId) => ({
          employeeId,
          owedAmount: Number(draftFor(employeeId).owedAmount) || undefined,
        })),
      }),
    );
    setSaving(false);
    const invoice = result.payload as { _id?: string } | undefined;
    if (invoice && invoice._id) {
      navigate(`/app/invoices/${invoice._id}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`New Invoice | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <SectionCard icon={<PostAdd />} title="New Invoice">
            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={12}>
              <TextField
                id="invoiceAgent"
                select
                label="Agent"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              >
                {agents.map((agent) => (
                  <MenuItem key={agent._id} value={agent._id}>
                    {agent.displayName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item md={3} xs={12}>
              <DatePicker
                label="Issue Date"
                disableMaskedInput
                views={["year", "month", "day"]}
                inputFormat="DD-MMM-YYYY"
                value={issueDate ? dayjs(issueDate) : null}
                onChange={(newValue: Dayjs | null) =>
                  setIssueDate(newValue ? newValue.toDate() : null)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>
              <Grid item md={5} xs={12}>
                <TextField
                  id="invoiceRemark"
                  label="Remark"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
            {!agentId && (
              <Typography color="textSecondary" sx={{ mt: 1 }}>
                Select an agent to list its departed employees that are not on
                any invoice yet.
              </Typography>
            )}
          </SectionCard>
        </LocalizationProvider>

        {success && <Toast severity="success">{success.message}</Toast>}
        {error && <Toast severity="error">{error.message}</Toast>}

        {agentId && (
          <>
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{ borderRadius: "8px" }}
            >
              <Typography variant="h6" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                Un-invoiced Departed Employees ({uninvoicedEmployees.length})
              </Typography>
              <Table size="small" aria-label="uninvoiced employees table">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell padding="checkbox">
                      <Checkbox checked={allSelected} onChange={toggleAll} />
                    </StyledTableCell>
                    <StyledTableCell>Full Name</StyledTableCell>
                    <StyledTableCell>Passport No.</StyledTableCell>
                    <StyledTableCell>{t("country")}</StyledTableCell>
                    <StyledTableCell>Departed On</StyledTableCell>
                    <StyledTableCell width="15%">
                      Owed Amount (USD)
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {loading === "pending" ? (
                    <TableSkeleton numRows={5} numColumns={6} />
                  ) : uninvoicedEmployees.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={6}>
                        No un-invoiced departed employees for this agent
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    uninvoicedEmployees.map((employee) => {
                      const draft = draftFor(employee._id);
                      return (
                        <StyledTableRow key={employee._id}>
                          <StyledTableCell padding="checkbox">
                            <Checkbox
                              checked={draft.selected}
                              onChange={(e) =>
                                setDraft(employee._id, {
                                  selected: e.target.checked,
                                })
                              }
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            {employee.givenName} {employee.surName}
                          </StyledTableCell>
                          <StyledTableCell>
                            {employee.passportNumber}
                          </StyledTableCell>
                          <StyledTableCell>
                            {te(
                              (employee.additionalData
                                ?.appliedCountry as string) ?? "",
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {employee.statusDates?.departed &&
                              format(
                                new Date(employee.statusDates.departed),
                                "dd-MMM-yyyy",
                              )}
                          </StyledTableCell>
                          <StyledTableCell>
                            <TextField
                              type="number"
                              value={draft.owedAmount}
                              onChange={(e) =>
                                setDraft(employee._id, {
                                  owedAmount: e.target.value,
                                })
                              }
                              variant="outlined"
                              size="small"
                              fullWidth
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Paper elevation={3} sx={{ mt: 2, p: 1.5, borderRadius: "8px" }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Typography variant="h6" component="div">
                  {selectedIds.length} employee(s) selected — Total:{" "}
                  <b>{totalOwed.toLocaleString()} USD</b>
                </Typography>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={selectedIds.length === 0 || saving}
                  onClick={handleSave}
                >
                  <Save /> {t("save")} Invoice
                </Button>
              </Stack>
            </Paper>
          </>
        )}
      </Box>
    </>
  );
};
